/**
 * EventManager - Manages seasonal event state, lifecycle, and player progress
 *
 * Handles event activation/deactivation, progress tracking, achievement unlocking,
 * and notifies subscribers of event changes.
 */

import type {
  SeasonalEvent,
  EventProgress,
  EventAchievement,
  EventChangeHandler,
  AchievementUnlockHandler,
  EventLocalState,
} from './types';
import { seasonalConfigService } from './SeasonalConfigService';

const STORAGE_KEY = 'space_invaders_2030_event_state';
const PROGRESS_KEY = 'space_invaders_2030_event_progress';
const ACHIEVEMENTS_KEY = 'space_invaders_2030_event_achievements';

export class EventManager {
  private static instance: EventManager;

  private currentEvent: SeasonalEvent | null = null;
  private progress: EventProgress | null = null;
  private unlockedAchievements: Map<string, EventAchievement> = new Map();

  private eventHandlers: Set<EventChangeHandler> = new Set();
  private achievementHandlers: Set<AchievementUnlockHandler> = new Set();

  private countdownInterval: number | null = null;
  private refreshInterval: number | null = null;

  private constructor() {
    this.loadState();
  }

  static getInstance(): EventManager {
    if (!EventManager.instance) {
      EventManager.instance = new EventManager();
    }
    return EventManager.instance;
  }

  /**
   * Initialize the event manager and fetch current event
   */
  async init(): Promise<SeasonalEvent | null> {
    try {
      const event = await seasonalConfigService.fetchCurrentEvent();
      this.setCurrentEvent(event);

      // Start periodic refresh (every 5 minutes)
      this.startRefreshInterval();

      return event;
    } catch (err) {
      console.error('Failed to initialize EventManager:', err);
      return null;
    }
  }

  /**
   * Load state from localStorage
   */
  private loadState(): void {
    try {
      // Load event state
      const stateJson = localStorage.getItem(STORAGE_KEY);
      if (stateJson) {
        const state: EventLocalState = JSON.parse(stateJson);
        if (state.cachedEvent && this.isEventActive(state.cachedEvent)) {
          this.currentEvent = state.cachedEvent;
        }
      }

      // Load progress
      const progressJson = localStorage.getItem(PROGRESS_KEY);
      if (progressJson) {
        this.progress = JSON.parse(progressJson);
      }

      // Load achievements
      const achievementsJson = localStorage.getItem(ACHIEVEMENTS_KEY);
      if (achievementsJson) {
        const achievements: EventAchievement[] = JSON.parse(achievementsJson);
        for (const achievement of achievements) {
          this.unlockedAchievements.set(achievement.id, achievement);
        }
      }
    } catch (err) {
      console.warn('Failed to load event state:', err);
    }
  }

  /**
   * Save state to localStorage
   */
  private saveState(): void {
    try {
      const state: EventLocalState = {
        currentEventId: this.currentEvent?.id || null,
        lastFetched: Date.now(),
        cachedEvent: this.currentEvent,
        progress: this.progress || this.createEmptyProgress(),
        achievements: Array.from(this.unlockedAchievements.values()),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

      if (this.progress) {
        localStorage.setItem(PROGRESS_KEY, JSON.stringify(this.progress));
      }

      localStorage.setItem(
        ACHIEVEMENTS_KEY,
        JSON.stringify(Array.from(this.unlockedAchievements.values()))
      );
    } catch (err) {
      console.warn('Failed to save event state:', err);
    }
  }

  /**
   * Create empty progress object
   */
  private createEmptyProgress(): EventProgress {
    return {
      eventId: this.currentEvent?.id || '',
      totalPoints: 0,
      totalAliensDestroyed: 0,
      gamesPlayed: 0,
      highestScore: 0,
      firstGameAt: 0,
      lastGameAt: 0,
    };
  }

  /**
   * Check if an event is currently active
   */
  private isEventActive(event: SeasonalEvent): boolean {
    const now = Date.now();
    return event.isActive && event.startDate <= now && event.endDate >= now;
  }

  /**
   * Set the current event and notify handlers
   */
  private setCurrentEvent(event: SeasonalEvent | null): void {
    const previousEvent = this.currentEvent;
    this.currentEvent = event;

    // Reset progress if event changed
    if (event && (!previousEvent || previousEvent.id !== event.id)) {
      this.progress = this.createEmptyProgress();
      this.progress.eventId = event.id;
    }

    // Clear progress if no event
    if (!event) {
      this.progress = null;
    }

    this.saveState();
    this.notifyEventHandlers(event);
  }

  /**
   * Start periodic refresh interval
   */
  private startRefreshInterval(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Refresh every 5 minutes
    this.refreshInterval = window.setInterval(() => {
      this.refresh();
    }, 5 * 60 * 1000);
  }

  /**
   * Stop all intervals
   */
  private stopIntervals(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Notify event handlers
   */
  private notifyEventHandlers(event: SeasonalEvent | null): void {
    for (const handler of this.eventHandlers) {
      try {
        handler(event);
      } catch (err) {
        console.error('Error in event handler:', err);
      }
    }
  }

  /**
   * Notify achievement handlers
   */
  private notifyAchievementHandlers(achievement: EventAchievement): void {
    for (const handler of this.achievementHandlers) {
      try {
        handler(achievement);
      } catch (err) {
        console.error('Error in achievement handler:', err);
      }
    }
  }

  /**
   * Subscribe to event changes
   */
  onEventChange(handler: EventChangeHandler): () => void {
    this.eventHandlers.add(handler);
    // Immediately call with current event
    handler(this.currentEvent);

    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to achievement unlocks
   */
  onAchievementUnlock(handler: AchievementUnlockHandler): () => void {
    this.achievementHandlers.add(handler);
    return () => {
      this.achievementHandlers.delete(handler);
    };
  }

  /**
   * Get the current active event
   */
  getCurrentEvent(): SeasonalEvent | null {
    return this.currentEvent;
  }

  /**
   * Get current progress
   */
  getProgress(): EventProgress | null {
    return this.progress;
  }

  /**
   * Get all unlocked achievements
   */
  getUnlockedAchievements(): EventAchievement[] {
    return Array.from(this.unlockedAchievements.values());
  }

  /**
   * Check if a specific achievement is unlocked
   */
  isAchievementUnlocked(achievementId: string): boolean {
    return this.unlockedAchievements.has(achievementId);
  }

  /**
   * Calculate bonus score for a kill
   */
  calculateBonusScore(baseScore: number, invaderKey?: string): number {
    if (!this.currentEvent?.bonusMultiplier) {
      return baseScore;
    }

    const { scoreMultiplier, invaderBonuses } = this.currentEvent.bonusMultiplier;
    let finalScore = baseScore * scoreMultiplier;

    // Apply invader-specific bonus if available
    if (invaderKey && invaderBonuses && invaderBonuses[invaderKey]) {
      finalScore = baseScore * invaderBonuses[invaderKey];
    }

    return Math.floor(finalScore);
  }

  /**
   * Record a game start
   */
  onGameStart(): void {
    if (!this.currentEvent || !this.progress) return;

    this.progress.gamesPlayed++;
    if (!this.progress.firstGameAt) {
      this.progress.firstGameAt = Date.now();
    }
    this.progress.lastGameAt = Date.now();

    this.saveState();
    this.checkAchievements();
  }

  /**
   * Record points earned
   */
  onPointsEarned(points: number): void {
    if (!this.currentEvent || !this.progress) return;

    this.progress.totalPoints += points;
    this.saveState();
    this.checkAchievements();
  }

  /**
   * Record alien destroyed
   */
  onAlienDestroyed(_invaderKey?: string): void {
    if (!this.currentEvent || !this.progress) return;

    this.progress.totalAliensDestroyed++;
    this.saveState();
    this.checkAchievements();

    // Track invader-specific destruction for achievements
    // This could be extended with more granular tracking if needed
  }

  /**
   * Record game end with final score
   */
  onGameEnd(finalScore: number): void {
    if (!this.currentEvent || !this.progress) return;

    if (finalScore > this.progress.highestScore) {
      this.progress.highestScore = finalScore;
    }

    this.saveState();
    this.checkAchievements();
  }

  /**
   * Check and unlock achievements
   */
  private checkAchievements(): void {
    if (!this.currentEvent?.achievements || !this.progress) return;

    for (const achievement of this.currentEvent.achievements) {
      // Skip already unlocked
      if (this.unlockedAchievements.has(achievement.id)) continue;

      let unlocked = false;

      // Check games played requirement
      if (achievement.gamesRequired !== undefined) {
        if (this.progress.gamesPlayed >= achievement.gamesRequired) {
          unlocked = true;
        }
      }

      // Check aliens destroyed requirement
      if (achievement.aliensRequired !== undefined) {
        if (this.progress.totalAliensDestroyed >= achievement.aliensRequired) {
          unlocked = true;
        }
      }

      // Check points requirement (highest score in single game)
      if (achievement.pointsRequired !== undefined) {
        if (this.progress.highestScore >= achievement.pointsRequired) {
          unlocked = true;
        }
      }

      if (unlocked) {
        this.unlockAchievement(achievement);
      }
    }
  }

  /**
   * Unlock an achievement
   */
  private unlockAchievement(achievement: EventAchievement): void {
    const unlockedAchievement: EventAchievement = {
      ...achievement,
      unlocked: true,
      unlockedAt: Date.now(),
    };

    this.unlockedAchievements.set(achievement.id, unlockedAchievement);
    this.saveState();
    this.notifyAchievementHandlers(unlockedAchievement);
  }

  /**
   * Get time remaining until event ends
   */
  getTimeRemaining(): { days: number; hours: number; minutes: number; seconds: number } | null {
    if (!this.currentEvent) return null;

    const now = Date.now();
    const remaining = this.currentEvent.endDate - now;

    if (remaining <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const seconds = Math.floor((remaining / 1000) % 60);
    const minutes = Math.floor((remaining / (1000 * 60)) % 60);
    const hours = Math.floor((remaining / (1000 * 60 * 60)) % 24);
    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));

    return { days, hours, minutes, seconds };
  }

  /**
   * Get formatted countdown string
   */
  getCountdownString(): string {
    const remaining = this.getTimeRemaining();
    if (!remaining) return '';

    const { days, hours, minutes, seconds } = remaining;

    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    } else {
      return `${seconds}s remaining`;
    }
  }

  /**
   * Refresh event data from server
   */
  async refresh(): Promise<SeasonalEvent | null> {
    try {
      const event = await seasonalConfigService.refresh();

      // Check if event changed or ended
      if (!event || (this.currentEvent && !this.isEventActive(this.currentEvent))) {
        this.setCurrentEvent(event);
      } else if (event && event.id !== this.currentEvent?.id) {
        this.setCurrentEvent(event);
      }

      return event;
    } catch (err) {
      console.error('Failed to refresh event:', err);
      return this.currentEvent;
    }
  }

  /**
   * Force check if current event is still active
   */
  checkEventStatus(): boolean {
    if (!this.currentEvent) return false;

    if (!this.isEventActive(this.currentEvent)) {
      this.setCurrentEvent(null);
      return false;
    }

    return true;
  }

  /**
   * Clear all event data (for testing/reset)
   */
  clearAllData(): void {
    this.currentEvent = null;
    this.progress = null;
    this.unlockedAchievements.clear();

    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PROGRESS_KEY);
      localStorage.removeItem(ACHIEVEMENTS_KEY);
    } catch (err) {
      console.warn('Failed to clear event data:', err);
    }

    this.notifyEventHandlers(null);
  }

  /**
   * Destroy the manager and clean up
   */
  destroy(): void {
    this.stopIntervals();
    this.eventHandlers.clear();
    this.achievementHandlers.clear();
  }
}

// Export singleton instance
export const eventManager = EventManager.getInstance();
